import { useCallback, useEffect, useRef, useState } from "react";
import { useStopOnSessionExpired } from '../auth/useStopOnSessionExpired';
import { isSessionExpired } from '../auth/sessionExpiry';

/**
 * Hook reutilizable de polling. Ejecuta `fetchFn` en un intervalo y se detiene
 * cuando `stopCondition(data)` retorna true, cuando el componente se desmonta,
 * o cuando se llama `stop()` manualmente.
 *
 * @param {() => Promise<any>} fetchFn              función async que retorna el siguiente state
 * @param {object}              opts
 * @param {number}              opts.intervalMs      ms entre fetches (default 2000)
 * @param {(data) => boolean}   opts.stopCondition   true → parar polling y disparar onStop (default: siempre continuar)
 * @param {(data) => void}      opts.onStop          callback al detener por stopCondition
 * @param {(err) => void}       opts.onError        callback en cada error del fetch
 * @param {boolean}             opts.autoStart       arrancar al montar (default false)
 * @returns {{data, error, isRunning, start, stop}}
 *
 * Ver ADR-017 / FSD-010 REQ-1014.
 */
export function usePolling(fetchFn, {
    intervalMs = 2000,
    stopCondition = () => false,
    onStop,
    onError,
    autoStart = false,
} = {}) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [isRunning, setIsRunning] = useState(false);
    const intervalRef = useRef(null);
    const isMountedRef = useRef(true);
    const fetchFnRef = useRef(fetchFn);
    const stopConditionRef = useRef(stopCondition);
    const onStopRef = useRef(onStop);
    const onErrorRef = useRef(onError);

    // Mantener las refs actualizadas sin re-arrancar el interval.
    useEffect(() => { fetchFnRef.current = fetchFn; }, [fetchFn]);
    useEffect(() => { stopConditionRef.current = stopCondition; }, [stopCondition]);
    useEffect(() => { onStopRef.current = onStop; }, [onStop]);
    useEffect(() => { onErrorRef.current = onError; }, [onError]);

    const stop = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (isMountedRef.current) setIsRunning(false);
    }, []);

    const tick = useCallback(async () => {
        try {
            const result = await fetchFnRef.current();
            if (!isMountedRef.current) return;
            setData(result);
            setError(null);
            if (stopConditionRef.current && stopConditionRef.current(result)) {
                stop();
                if (onStopRef.current) onStopRef.current(result);
            }
        } catch (err) {
            if (!isMountedRef.current) return;
            setError(err);
            if (onErrorRef.current) onErrorRef.current(err);
        }
    }, [stop]);

    const start = useCallback(() => {
        if (intervalRef.current) return;
        // Con la sesion muerta el tick solo produce 401. La guarda va aca y no solo en el
        // suscriptor porque un componente puede montar DESPUES de que la sesion se marco (#172).
        if (isSessionExpired()) return;
        setIsRunning(true);
        // Primer tick inmediato para no esperar `intervalMs` antes de la primera lectura.
        tick();
        intervalRef.current = setInterval(tick, intervalMs);
    }, [tick, intervalMs]);

    // Con la sesion muerta el tick solo produce 401. Frenarlo es lo que hace que la rafaga
    // desaparezca de los logs de la API y no solo que el usuario vea un aviso (#168). Y si la
    // sesion se recupera hay que volver a arrancar, o queda una pantalla congelada sin que nada
    // se lo diga al usuario (#172).
    useStopOnSessionExpired(stop, start);

    useEffect(() => {
        isMountedRef.current = true;
        if (autoStart) start();
        return () => {
            isMountedRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [autoStart, start]);

    return { data, error, isRunning, start, stop };
}
